import { flow, pipe } from 'fp-ts/function';
import {
    __,
    compact,
    cond,
    constant,
    filter,
    find,
    first,
    get,
    has,
    head,
    identity,
    isEmpty,
    isNil,
    map,
    merge,
    negate,
    over,
    pick,
    sortBy,
    spread,
    values,
} from 'lodash/fp';
import { createSelector } from 'reselect';

import { getShortDayNameKey } from '@mews-distributor/common';
import { hourlyServiceDialogUtils } from '@mews/business-components';
import { PricingMode, UnicodeEntity } from '@mews/enums';
import { compactArray, getNumberProp, getOrEmptyObject, getProp, getStringProp } from '@mews/fp';
import { DateTimeLocalizationInfo, NumberService, pricingModeConfiguration, TextComponent } from '@mews/framework';
import {
    Date,
    DateService,
    DateTime,
    DateTimeService,
    DateTimeUtcService,
    Time,
    TimeService,
    WallClockDate,
    WallClockDateService,
} from '@mews/framework/datetime';

import { Optional } from '@mews/std';
import { parentSelector } from '../../selectors/parentSelector';
import { getSelectedEnterpriseDefaultLanguageCode } from '../../selectors/views/selectedEnterprise/selectedEnterprise';
import { ApplicationState } from '../../types';
import { asProp, memoizeWithResolver } from '../../utils/func';
import { logError } from '../../utils/logError';
import { getCurrencyCode, getLanguageCode } from '../app/selectors';
import { dayNameKeys, dayShortNameKeys, monthNameKeys, monthShortNameKeys } from './dateTimeNames';
import { localeSortBy } from './localeSortBy';

const getGlobalization = createSelector(parentSelector, get('globalization'));

const getCountries = createSelector(getGlobalization, get('countries'));

const getCultureInfos = createSelector(getGlobalization, get('cultureInfos'));

const getCurrencies = createSelector(getGlobalization, get('currencies'));

export const getLanguages = createSelector(getGlobalization, get('languages'));

export const getTexts = createSelector(getGlobalization, get('texts'));
export const getTaxRates = createSelector(getGlobalization, get('taxRates'));

const getCultureInfo = createSelector(getStringProp('cultureCode'), getCultureInfos, get);

export const hasCurrencyCode = createSelector(getStringProp('currencyCode'), getCurrencies, has);

export const getValidCurrencyCode = createSelector(
    hasCurrencyCode,
    getStringProp('currencyCode'),
    (isValidCurrencyCode, currencyCode) => (isValidCurrencyCode ? currencyCode : null)
);

export const getCurrencyCodes = createSelector(getCurrencies, map('code'));

const getDefaultLanguage = createSelector(getLanguages, find({ isDefault: true }));

const getDefaultLanguageCode = flow(getDefaultLanguage, get('code'));

export const hasLanguageCode = createSelector(getStringProp('languageCode'), getLanguages, has);
export const getValidLanguageCode = createSelector(
    hasLanguageCode,
    getStringProp('languageCode'),
    (isValidLanguage, languageCode) => (isValidLanguage ? languageCode : null)
);

export const getSortedLanguages = createSelector(getLanguages, flow(values, localeSortBy('localName')));

const getCountryCodeTextKey = flow(get('code'), (code) => `Country${code}` as Mews.TextKey);

export const getSortedCountries = (state: $TSFixMe, { languageCode = getLanguageCode(state) } = {}) =>
    memoizedSortedCountries(state, { languageCode });
const memoizedSortedCountries = memoizeWithResolver(
    (state: ApplicationState) => {
        const countries = getCountries(state);
        const localizedCountries = map(
            (country) => ({
                ...country,
                name: localizeText(getCountryCodeTextKey(country))(state),
            }),
            countries
        );
        const sortedCountries = localeSortBy('name', localizedCountries);

        return sortedCountries;
    },
    (_state: ApplicationState, { languageCode }: { languageCode: string }) => `Countries-${languageCode}`
);

export const getSortedCurrencies = createSelector(getCurrencies, sortBy('code'));

const pickLanguageCode = flow(over([getStringProp('languageCode'), getLanguageCode]), compact, head);

// localization
const getTextLocalizationInfo = createSelector(pickLanguageCode, getLanguages, (languageCode, languages) => {
    const languageChain = [languageCode];
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    let fallbackLanguageCode = get([languageCode, 'fallbackLanguageCode'], languages);

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    while (fallbackLanguageCode) {
        // @ts-expect-error  Argument of type 'LodashGet1x2<any>' is not assignable to parameter of type 'string'.ts(2345)
        // For safety, there might be an acyclity check, but as we control the data, we know there is none
        languageChain.push(fallbackLanguageCode);
        // @ts-expect-error $TSFixMeError: No overload matches this call.
        fallbackLanguageCode = get([fallbackLanguageCode, 'fallbackLanguageCode'], languages);
    }

    return {
        languageCodes: languageChain,
    };
});

export const getLanguageChain = flow(getTextLocalizationInfo, ({ languageCodes }) => languageCodes, compactArray);

const getCurrency = createSelector(getStringProp('currencyCode'), getCurrencies, get);

export const getCurrencyPrecision = createSelector(getCurrency, get('precision'));

const getCurrencyCultureFormats = createSelector(getCurrency, get('cultureFormats'));

const getCurrencyCultureFormat = createSelector(getStringProp('cultureCode'), getCurrencyCultureFormats, get);

const getCurrencyLocalizationInfo = createSelector(
    getCurrency,
    getCurrencyCultureFormat,
    flow(merge, pick(['symbol', 'symbolPosition', 'symbolSpacing', 'precision']))
);

const getDateTimeCultureInfo = createSelector(getCultureInfo, getOrEmptyObject('dateTime'));

const getCurrencyCultureInfo = createSelector(getCultureInfo, getOrEmptyObject('currency'));

const getNumberCultureInfo = createSelector(getCultureInfo, getOrEmptyObject('number'));

// text translation
const createLocalizeText =
    (textsSelector: $TSFixMe) =>
    (localizationInfoSelector: $TSFixMe) =>
    (options: { fallbackToKey: boolean; highlightMissingKey: boolean }) =>
    (key: Mews.TextKey, substitutes = {}) =>
    (state: ApplicationState) => {
        const texts = textsSelector(state);
        const localizationInfo = localizationInfoSelector(state);
        return TextComponent.translate(texts, key, localizationInfo, substitutes, options);
    };

const localizeTextWithOptions = createLocalizeText(getTexts)(getTextLocalizationInfo);
// @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 0.
export const localizeText = localizeTextWithOptions();

export const hasTextTranslation = createSelector(
    getStringProp('textKey'),
    getStringProp('languageCode'),
    getTexts,
    (textKey, languageCode, texts) => Boolean(get([languageCode, textKey], texts))
);

// city localization
export const createLocalizeCity = (state: $TSFixMe) => {
    const languageCode = getLanguageCode(state);
    // @ts-expect-error $TSFixMeError: Argument of type 'string | undefined' is not assignable to parameter of type 'PropertyPath'.
    const languageCondition = [has(languageCode), get(languageCode)];

    const defaultLanguageCode = getDefaultLanguageCode(state);
    const defaultLanguageCondtion = [has(defaultLanguageCode), get(defaultLanguageCode)];
    // @ts-expect-error $TSFixMeError: Type '() => string' is not assignable to type '(val: object | null | undefined) => boolean | undefined'.
    return cond([[isEmpty, constant('')], languageCondition, defaultLanguageCondtion]);
};

// date time names translation
const localizeNames = (keys: Mews.TextKey[]) => (state: ApplicationState) =>
    map((key: Mews.TextKey) => localizeText(key)(state))(keys);

const getMonthNames = localizeNames(monthNameKeys);
const getMonthShortNames = localizeNames(monthShortNameKeys);
const getDayNames = localizeNames(dayNameKeys);
const getDayShortNames = localizeNames(dayShortNameKeys);

const getDateTimeLocalizationInfo = memoizeWithResolver(
    flow(
        over([
            (_state, { cultureCode }) => cultureCode,
            getDateTimeCultureInfo,
            getMonthNames,
            getMonthShortNames,
            getDayNames,
            getDayShortNames,
        ]),
        ([cultureCode, cultureInfo, monthNames, monthShortNames, dayNames, dayShortNames]) => ({
            cultureCode,
            ...cultureInfo,
            monthNames,
            monthShortNames,
            dayNames,
            dayShortNames,
        })
    ),
    (_state: $TSFixMe, { cultureCode }: $TSFixMe) => cultureCode
);

export const getCurrentDateTimeLocalizationInfo: (state: ApplicationState) => DateTimeLocalizationInfo = flow(
    over([identity, flow(getLanguageCode, over([asProp('languageCode'), asProp('cultureCode')]), spread(merge))]),
    spread(getDateTimeLocalizationInfo)
);

// property localization
const getPropertyFromProps = getStringProp('property');

const toLanguageFromProps = flow(over([getStringProp('languageCode'), getPropertyFromProps]), spread(get));

const toCurrentLanguage = flow(over([getLanguageCode, getPropertyFromProps]), spread(get));

const toSelectedEnterpriseDefaultLanguage = flow(
    over([getSelectedEnterpriseDefaultLanguageCode, getPropertyFromProps]),
    spread(get)
);

const toAnyLanguage = flow(getPropertyFromProps, values, first);

export const localizeProperty = flow(
    over([toLanguageFromProps, toCurrentLanguage, toSelectedEnterpriseDefaultLanguage, toAnyLanguage, constant('')]),
    filter(negate(isNil)),
    first
);

export const createLocalizePropertySelector = (propertySelector: $TSFixMe) =>
    flow(over([identity, flow(propertySelector, asProp('property'))]), spread(localizeProperty));

export const localizeDate = createSelector(
    getProp<WallClockDate>()('date'),
    getCurrentDateTimeLocalizationInfo,
    WallClockDateService.toString
);

export const localizeShortTime = createSelector(
    getProp<DateTime>()('dateTime'),
    getCurrentDateTimeLocalizationInfo,
    (dateTime, localizationInfo) =>
        pipe(DateTimeService.toTime(dateTime), (time) => TimeService.toShortString(time, localizationInfo))
);

export const localizeShortDate = createSelector(
    getProp<Date>()('date'),
    getCurrentDateTimeLocalizationInfo,
    (date: Optional<Date>, localizationInfo: DateTimeLocalizationInfo) =>
        date ? DateService.toShortString(date, localizationInfo) : ''
);

interface LocalizeShortDateAndTimeStringProps {
    dateTimeUtc: string;
    timezone: string;
}

export const localizeShortDateAndTimeString = (
    state: ApplicationState,
    { dateTimeUtc, timezone }: LocalizeShortDateAndTimeStringProps
) => {
    const parsedDateTime = pipe(DateTimeUtcService.parse(dateTimeUtc), (parsedDateTimeUtc) =>
        DateTimeService.fromUtc(parsedDateTimeUtc, timezone)
    );
    const parsedDate = DateTimeService.toDate(parsedDateTime);
    const shortDateString = localizeShortDate(state, { date: parsedDate });
    const shortDateTimeString = localizeShortTime(state, { dateTime: parsedDateTime });
    return { dateString: shortDateString, shortDateTimeString };
};

export const localizePercent = createSelector(
    getNumberProp('number'),
    getNumberCultureInfo,
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    getProp()('options'),
    NumberService.formatPercent
);

const getPricingModeKey = get(__, pricingModeConfiguration);

export const localizeCurrencyValue = createSelector(
    identity,
    getStringProp('languageCode'),
    getProp()('currencyValue'),
    getProp()('pricingMode'),
    getProp()('optionalCurrencyCode'),
    (state, languageCode, currencyValue, pricingMode, optionalCurrencyCode = null) => {
        if (isEmpty(currencyValue)) {
            return '';
        }
        // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'PricingMode | [PricingMode]'.
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const pricingModeKey = getPricingModeKey(pricingMode || PricingMode.Gross);

        const currencyCodeSimpleMode = get('currencyCode', currencyValue);
        // @ts-expect-error $TSFixMeError: Argument of type 'undefined' is not assignable to parameter of type 'ApplicationState'.
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const currencyCodeGrossNet = optionalCurrencyCode || getCurrencyCode(state);

        const currencyCode = pricingMode === PricingMode.Simple ? currencyCodeSimpleMode : currencyCodeGrossNet;
        // @ts-expect-error $TSFixMeError: Argument of type 'undefined' is not assignable to parameter of type 'ApplicationState'.
        const currencyLocalizationInfo = getCurrencyLocalizationInfo(state, {
            currencyCode,
            cultureCode: languageCode,
        });
        // @ts-expect-error $TSFixMeError: Argument of type 'undefined' is not assignable to parameter of type 'ApplicationState'.
        const currencyCultureInfo = getCurrencyCultureInfo(state, { cultureCode: languageCode });
        const priceValues = pricingMode === PricingMode.Simple ? currencyValue : get(currencyCode, currencyValue);
        const value = get(pricingModeKey, priceValues);

        // the case when we change a currency and we make a request to get prices related to an update currency
        // the state of items will be updated but the state of the current currency is updated later, but
        // we still have a listener to calculate the prices when the state is changed, so we are getting
        // an error when we have prices with an updated currency but the currency wasn't updated yet.
        if (value == null) {
            logError("The item doesn't have a value for the selected currency");
            return '';
        }

        const localizationInfo = {
            ...currencyLocalizationInfo,
            ...currencyCultureInfo,
        };

        return NumberService.formatCurrencyValue(value, localizationInfo);
    }
);

/** @returns Fri 7/21/2023 for en-US */
export const localizeDateWithDay = (state: ApplicationState, { date }: { date: WallClockDate }) =>
    `${localizeText(getShortDayNameKey(date))(state)} ${localizeDate(state, { date })}`;

/** @returns format Fri 7/21/2023 ‐ Sun 7/23/2023 for en-US */
export const getDateRange = (
    state: ApplicationState,
    { startDate, endDate }: { startDate: WallClockDate; endDate: WallClockDate }
) =>
    [
        localizeDateWithDay(state, { date: startDate }),
        ...(!WallClockDateService.isEqual(startDate, endDate)
            ? [
                  UnicodeEntity.NoBreakSpace,
                  UnicodeEntity.Dash,
                  UnicodeEntity.NoBreakSpace,
                  localizeDateWithDay(state, { date: endDate }),
              ]
            : []),
    ].join('');

/** @returns 3:00 PM ‐ 12:00 PM for en-US */
export const getTimeRange = (state: ApplicationState, { startTime, endTime }: { startTime: Time; endTime: Time }) => {
    const localizationInfo = getCurrentDateTimeLocalizationInfo(state);

    return hourlyServiceDialogUtils.getTimeRange({ startTime, endTime, localizationInfo });
};
